"use client"

import { useMemo } from "react"

import type { AnyRecord } from "../../personal-web"
import type { EsimStatus } from "../types"

/**
 * Classification of an eSIM into the business-level lifecycle states the
 * install page reasons about. The mapping is one-way and intentionally lossy:
 * the renderer should branch on EsimStatus only, never on raw backend strings.
 *
 * Update this map (not the renderer) when backend introduces a new status —
 * one place to keep in sync.
 */
const STATUS_MAP: Record<string, EsimStatus> = {
  // Pending / not yet installed
  pending: "pending",
  unused: "pending",
  new: "pending",
  provisioned: "pending",
  reserved: "pending",

  // In-progress install handoff (user tapped deeplink, awaiting carrier ack)
  installing: "installing",
  downloading: "installing",
  activating: "installing",

  // Installed on device but data hasn't started flowing
  installed: "installed",
  enabled: "installed",
  ready: "installed",

  // Connected and using data
  active: "active",
  in_use: "active",
  inuse: "active",
  online: "active",
  connected: "active",

  // Terminal: window elapsed
  expired: "expired",
  ended: "expired",
  finished: "expired",
  canceled: "expired",
  cancelled: "expired",

  // Terminal-ish: paused but recoverable
  suspended: "paused",
  paused: "paused",
  disabled: "paused",
}

export type EsimStateAPI = {
  status: EsimStatus
  /** True for installed | active | paused (profile lives on a device). */
  isInstalled: boolean
  /** True only when data is actively running. */
  isActive: boolean
  /** True for expired (cannot be revived by the user). */
  isTerminal: boolean
  /** Raw backend string — kept for debugging / analytics, not for branching. */
  raw: string
}

function normalize(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_")
}

function pickStatus(esim: AnyRecord): string {
  // Multiple fields in the wild — first non-empty wins, matches pickValue's
  // contract used elsewhere in the codebase.
  const candidates = [
    "status",
    "esim_status",
    "state",
    "activation_status",
    "lifecycle_status",
  ]
  for (const key of candidates) {
    const raw = esim?.[key]
    if (raw != null && `${raw}`.trim()) return `${raw}`
  }
  return ""
}

/**
 * Classifies a raw eSIM record into our lifecycle state. Keep this hook
 * pure / no side effects so it's safe to memoise on the parent's render.
 */
export function useEsimState(esim: AnyRecord | null | undefined): EsimStateAPI {
  return useMemo(() => {
    const raw = esim ? pickStatus(esim) : ""
    const status: EsimStatus = STATUS_MAP[normalize(raw)] ?? "unknown"
    return {
      status,
      isInstalled:
        status === "installed" || status === "active" || status === "paused",
      isActive: status === "active",
      isTerminal: status === "expired",
      raw,
    }
  }, [esim])
}
